import { useEffect, useRef } from 'react';

/**
 * Writes normalised pointer position (-1..1) to --tilt-x / --tilt-y on the
 * returned ref. Transform-only, one write per frame, and skipped entirely on
 * touch devices and under reduced-motion.
 */
export function usePointerTilt<T extends HTMLElement>() {
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!fine || reduced) return;

    let frame = 0;

    const onMove = (event: PointerEvent) => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const x = (event.clientX / window.innerWidth) * 2 - 1;
        const y = (event.clientY / window.innerHeight) * 2 - 1;
        element.style.setProperty('--tilt-x', x.toFixed(3));
        element.style.setProperty('--tilt-y', y.toFixed(3));
      });
    };

    window.addEventListener('pointermove', onMove, { passive: true });

    return () => {
      window.removeEventListener('pointermove', onMove);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return ref;
}
